import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { ItemGroupPreview } from '@dto/architecture.dto';
import { Button, Form } from 'antd';
import { useContext, useEffect, useRef } from 'react';
import { GLOB } from 'src/util/Glob';
import { AllItemsContext } from '../comp/AllItems';
import { AllTagsContext } from '../comp/AllTags';
import { makePreviewApiData } from '../comp/preview/CgPreviewHandler';
import { usePreviewModal } from '../comp/preview/Preview';
import TagSelect from '../comp/TagSelect';
import { ArrayElement, dependenciesMaker } from '../parent/TagList';
import { AdditionalApiPropsContext, invalidHwProps, RuleContext } from '../XmRule';
import { RuleGroup } from '../XmRuleList';
import AddButton from './AddButton';

interface Props {
  formItemName?: (string | number)[];
}

function TagList({ formItemName = ['tags'] }: Props) {
  const { cls, subsystem, hw_type } = useContext(AllItemsContext);
  const { fetchStarted, startFetch } = useContext(AllTagsContext);
  const { linuxExclude, getPath, getValue, mountPoint } = useContext(RuleContext);
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem });
  const [showPreview, contextHolder] = usePreviewModal();
  const items = formItemName.includes('exclude') ? getValue<RuleGroup['exclude']['tags']>(['exclude', 'tags']) : getValue<RuleGroup['tags']>('tags');
  const init = useRef<() => void>(undefined);
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);

  useEffect(() => {
    if (!fetchStarted)
      startFetch();

    init.current?.();
  }, []);

  const handlePreview = (tag: string) => () => {
    const parents = getValue<RuleGroup['parents']>('parents');
    if (!formItemName.includes('exclude')) {
      const data = {
        ...makePreviewApiData({
          cls,
          hwType: hw_type,
          subsystem,
          parents: formItemName.includes('exclude') ? undefined : parents,
          tags: [{ tag_id: tag, tag: '' }]
        }, linuxExclude),
        ...additionalPayload
      };

      showPreview({
        subsystem,
        hw_type,
        data,
        additionalApiParams: preview?.items?.item,
      });

      return;
    }

    const regex = getValue<RuleGroup['regex']>('regex');
    const tags = getValue<RuleGroup['tags']>('tags');
    const data: ItemGroupPreview[] = [
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, tags: [{ tag_id: tag, tag: '' }] }, linuxExclude,), ...additionalPayload },
      { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, parents, regex, tags }, linuxExclude), ...additionalPayload },
    ];

    showPreview({
      subsystem,
      hw_type,
      data,
      additionalApiParams: preview?.exclude?.item ? { ...preview.exclude.item, intersection: true } : { intersection: true },
    });
  };

  const getDependencies = dependenciesMaker([...mountPoint, ...formItemName], ['tag_id']);
  const noSameTag = sameTagRuleMaker(items);

  const handleRemove = (remove: (index: number | number[]) => void, index: number) => () => {
    remove(index);
  };

  const { readonly } = GLOB.userInfo;

  return <>
    <Form.List name={getPath(formItemName)}>
      {(tagsItems, { remove, add }) => {
        if (!init.current && !formItemName.includes('exclude') && !items) {
          init.current = () =>
            add({
              tag_id: undefined,
              tag: undefined
            });
        }

        return <>
          <div className='xm-rule-filter-tag'>
            {tagsItems.map(({ key, name }) => {
              const tag = formItemName.includes('exclude') ?
                getValue<ArrayElement<RuleGroup['exclude']['tags']>>(['exclude', 'tags', name]) :
                getValue<ArrayElement<RuleGroup['tags']>>(['tags', name]);
              return <span className='xm-tag-item' data-testid='tag-rule-row' key={`parent-tag-list-${key}`}>
                <span className='xm-tag-item-container'>
                  <Form.Item
                    name={[name, 'tag_id']}
                    dependencies={getDependencies(tagsItems.length, name)}
                    help={<></>}
                    rules={[
                      { required: true },
                      noSameTag(name)
                    ]}
                  >
                    <TagSelect selectProps={{ className: 'xm-tag-select' }} />
                  </Form.Item>
                  <Button
                    disabled={invalidBaseProps || !tag.tag_id}
                    size='small'
                    icon={<EyeOutlined />}
                    onClick={handlePreview(tag.tag_id)}
                  />
                </span>
                <Button
                  size='small'
                  className={readonly || invalidBaseProps || tagsItems.length < 2 ? 'xm-disabled-delete' : 'hover-danger'}
                  disabled={readonly || invalidBaseProps || tagsItems.length < 2}
                  type='link'
                  title='Remove tag'
                  icon={<DeleteOutlined onClick={handleRemove(remove, name)} />}
                />
              </span>
            })}
          </div>
          <AddButton onClick={() => add({
            tag_id: undefined,
            tag: undefined
          })} />
        </>
      }}
    </Form.List>
    {contextHolder}
  </>;
}

const sameTagRuleMaker = (tags: {
  tag_id: string;
  tag: string;
}[]) => (tagIndx: number) => {
  const validator = (_, val: string) => {
    if (tags.some((tag, indx) => tag.tag_id == val && indx !== tagIndx))
      return Promise.reject(new Error(`Tag with ID ${val} already exists.`));

    return Promise.resolve();
  }

  return { validator }
};

export default TagList;